import React, { useState, useEffect, ReactNode, useCallback } from 'react';
import clsx from 'clsx';
import Dismiss from '../Dismiss/Dismiss';
import Chevron, { Direction } from '../icons/Chevron/Chevron';
import Tag from '../icons/Tag/Tag';
import styles from './SlideableDrawer.module.css';
import { useTheme } from '../../../hooks/useTheme';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';

interface SlideableDrawerProps {
  openOnLoad?: boolean;
  title?: string;
  subtitle?: string;
  titleStyle?: React.CSSProperties;
  showTitleIcon?: boolean;
  children: ReactNode;
  delaySeconds?: number;
  onDismiss?: (source: 'button' | 'overlay' | 'swipe') => void;
  onOpen?: () => void;
  hideBannerTitle?: boolean;
  // Blur the page behind the open sheet. Defaults to hideBannerTitle,
  // which is how the sheet has always behaved.
  blurOverlay?: boolean;
  shouldClose?: boolean;
  fullHeight?: boolean;
  renderHeader?: (dismiss: () => void) => ReactNode;
}

const DRAWER_STATE = {
  HIDDEN: 'hidden',
  BANNER: 'banner',
  OPEN: 'open',
  ANIMATING_TO_BANNER: 'animatingToBanner',
} as const;

type DrawerState = (typeof DRAWER_STATE)[keyof typeof DRAWER_STATE];

const SlideableDrawerBanner = ({
  children,
  title = 'Special Offer',
  subtitle,
  titleStyle,
  showTitleIcon = true,
  delaySeconds = 0,
  onDismiss,
  onOpen,
  hideBannerTitle = false,
  blurOverlay = hideBannerTitle,
  shouldClose = false,
  fullHeight = false,
  renderHeader,
}: SlideableDrawerProps) => {
  const {
    headersMediumNone: mediumHeaderStyle,
    bodytextDefaultNone: bodyTextStyle,
  } = useTheme();
  const [drawerState, setDrawerState] = useState<DrawerState>(
    DRAWER_STATE.HIDDEN,
  );
  const [delayElapsed, setDelayElapsed] = useState(delaySeconds <= 0);
  const logEvent = useAnalyticsPost();

  const baseLogInfo = {
    feature: 'customComponent',
    pageComponent: NarvarEventComponents.CUSTOM_COMPONENT_DRAWER,
    eventComponent: NarvarEventComponents.CUSTOM_COMPONENT_DRAWER,
    eventFeature: NarvarEventComponents.CUSTOM_COMPONENT_DRAWER,
  };

  const dismissDrawer = useCallback(
    (source: 'overlay' | 'button' | 'swipe') => {
      setDrawerState(DRAWER_STATE.ANIMATING_TO_BANNER);
      setTimeout(() => {
        setDrawerState(DRAWER_STATE.BANNER);
        onDismiss?.(source);
      }, 300);

      if (navigator.vibrate) {
        navigator.vibrate(10);
      }

      logEvent({
        ...baseLogInfo,
        clickSubType: NarvarClickSubTypes.DISMISS,
        clickType: NarvarClickTypes.INTERACTION,
        clickCategory: source,
        eventType: NarvarEventTypes.CLICK,
      });
    },
    [onDismiss, logEvent],
  );

  useEffect(() => {
    logEvent({
      ...baseLogInfo,
      eventType: NarvarEventTypes.COMPONENT_RENDER,
    });
  }, []);

  useEffect(() => {
    if (delaySeconds > 0) {
      const timer = setTimeout(
        () => setDelayElapsed(true),
        delaySeconds * 1000,
      );
      return () => clearTimeout(timer);
    }
  }, [delaySeconds]);

  useEffect(() => {
    if (delayElapsed && drawerState === DRAWER_STATE.HIDDEN) {
      setDrawerState(DRAWER_STATE.OPEN);
      onOpen?.();
    }
  }, [delayElapsed]);

  useEffect(() => {
    if (shouldClose && drawerState !== DRAWER_STATE.HIDDEN) {
      dismissDrawer('button');
    }
  }, [shouldClose]);

  const isOpen = drawerState === DRAWER_STATE.OPEN;
  useBodyScrollLock(isOpen);

  const openOnClick = () => {
    setDrawerState(DRAWER_STATE.OPEN);
    logEvent({
      ...baseLogInfo,
      clickSubType: NarvarClickSubTypes.REOPEN,
      clickType: NarvarClickTypes.INTERACTION,
      clickCategory: 'banner',
      eventType: NarvarEventTypes.CLICK,
    });
  };

  const showOverlay = isOpen;

  const containerClasses = clsx(
    styles.container,
    styles.containerBanner,
    drawerState === DRAWER_STATE.BANNER && styles.bannerOpen,
    isOpen && styles.animateUpFromBanner,
    isOpen && fullHeight && styles.containerFullHeight,
    drawerState === DRAWER_STATE.ANIMATING_TO_BANNER &&
      styles.animateDownToBanner,
    drawerState === DRAWER_STATE.HIDDEN && styles.completelyHidden,
  );

  const bannerClasses = clsx(
    styles.bannerContainer,
    hideBannerTitle && styles.bannerContainerHidden,
    drawerState === DRAWER_STATE.BANNER && styles.bannerContainerCollapsed,
    drawerState === DRAWER_STATE.BANNER && styles.bannerContainerClickable,
  );

  const overlayClasses = clsx(
    showOverlay ? styles.overlay : styles.overlayHidden,
    blurOverlay && styles.overlayBlur,
  );

  return (
    <>
      <div className={overlayClasses} />
      <div className={containerClasses}>
        {renderHeader ? (
          renderHeader(() => dismissDrawer('button'))
        ) : (
          <div
            className={bannerClasses}
            onClick={
              drawerState === DRAWER_STATE.BANNER ? openOnClick : undefined
            }
          >
            {hideBannerTitle && isOpen && (
              <div className={styles.dismissContainer}>
                <Dismiss
                  fill="#4B4B4B"
                  onClick={() => dismissDrawer('button')}
                />
              </div>
            )}
            {!hideBannerTitle && (
              <>
                <div
                  className={styles.bannerText}
                  style={{ ...mediumHeaderStyle, ...titleStyle }}
                >
                  {showTitleIcon && <Tag s={{ marginRight: '0.5rem' }} />}
                  {title}
                </div>
                <div className={styles.dismissContainerBanner}>
                  {isOpen ? (
                    <Dismiss
                      fill="#4B4B4B"
                      onClick={() => dismissDrawer('button')}
                    />
                  ) : (
                    <Chevron direction={Direction.Up} />
                  )}
                </div>
                {subtitle && (
                  <p
                    className={styles.bannerSubtitle}
                    style={{ fontFamily: bodyTextStyle?.fontFamily }}
                  >
                    {subtitle}
                  </p>
                )}
              </>
            )}
          </div>
        )}
        {children}
      </div>
    </>
  );
};

export default SlideableDrawerBanner;
